/**
 * FYSHE - Kaktus Contact Form Builder
 * Multi-step navigation + date validation
 *
 * Features:
 * 1. Step 2 is locked until customer successfully progresses from Step 1.
 * 2. Step 3 is locked until customer successfully progresses to Step 3.
 * 3. Previously reached steps remain accessible.
 * 4. Future steps cannot be clicked directly from the wizard.
 * 5. Requested Delivery Date (field_5) is manually validated.
 * 6. Conditional Kaktus step skipping remains supported.
 *
 * Form structure:
 * Wizard:
 *   .k-popup-wizard__li[data-step="1"]
 *   .k-popup-wizard__li[data-step="2"]
 *   .k-popup-wizard__li[data-step="3"]
 *
 * Content:
 *   .k-popup__content[data-step="1"]
 *   .k-popup__content[data-step="2"]
 *   .k-popup__content[data-step="3"]
 *
 * Requested Delivery Date:
 *   data-field-id="NmQY2"
 *   name="field_5"
 */

(function () {
  'use strict';

  const WIZARD_SELECTOR = '.k-popup-wizard';
  const STEP_SELECTOR = '.k-popup-wizard__li[data-step]';

  const DATE_FIELD_SELECTOR =
    'label[data-field-id="NmQY2"] input[name="field_5"]';

  const DATE_WRAPPER_SELECTOR =
    'label[data-field-id="NmQY2"]';

  const DATE_ERROR_CLASS =
    'kaktus-date-validation-error';

  const LOCKED_CLASS =
    'kaktus-step-locked';

  const DATE_ERROR_MESSAGE =
    'Please select a requested delivery date.';

  /*
   * Highest step that customer has legitimately reached.
   *
   * Initial state:
   * Step 1 = available
   * Step 2 = locked
   * Step 3 = locked
   */
  let highestReachedStep = 1;


  /**
   * ----------------------------------------------------------
   * DATE VALIDATION
   * ----------------------------------------------------------
   */

  function getDateField() {
    return document.querySelector(DATE_FIELD_SELECTOR);
  }


  function getDateWrapper() {
    return document.querySelector(DATE_WRAPPER_SELECTOR);
  }


  function isElementVisible(element) {
    if (!element) {
      return false;
    }

    const style = window.getComputedStyle(element);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.offsetParent !== null
    );
  }


  function removeDateError() {
    const wrapper = getDateWrapper();
    const input = getDateField();

    if (wrapper) {
      const error = wrapper.querySelector(
        '.' + DATE_ERROR_CLASS
      );

      if (error) {
        error.remove();
      }
    }

    if (input) {
      input.removeAttribute('aria-invalid');
    }
  }


  function showDateError() {
    const wrapper = getDateWrapper();
    const input = getDateField();

    if (!wrapper || !input) {
      return;
    }

    removeDateError();

    input.setAttribute('aria-invalid', 'true');

    const error = document.createElement('div');

    error.className = DATE_ERROR_CLASS;
    error.textContent = DATE_ERROR_MESSAGE;

    error.style.marginTop = '6px';
    error.style.fontSize = '13px';
    error.style.lineHeight = '1.4';
    error.style.color = '#d93025';

    const inputWrap =
      wrapper.querySelector('.date-input-wrap');

    if (inputWrap) {
      inputWrap.insertAdjacentElement(
        'afterend',
        error
      );
    } else {
      wrapper.appendChild(error);
    }
  }


  function validateDateField() {
    const input = getDateField();
    const wrapper = getDateWrapper();

    if (!input || !wrapper) {
      return true;
    }

    /*
     * Only validate when Step 2 / field is actually visible.
     *
     * This is important because your Kaktus form contains
     * conditional navigation.
     */
    if (!isElementVisible(wrapper)) {
      return true;
    }

    const value = (input.value || '').trim();

    if (!value) {
      showDateError();
      return false;
    }

    removeDateError();

    return true;
  }


  /**
   * ----------------------------------------------------------
   * DETECT CURRENT KAKTUS STEP
   * ----------------------------------------------------------
   */

  function getCurrentStep() {
    const activeStep = document.querySelector(
      '.k-popup-wizard__li.active[data-step]'
    );

    if (activeStep) {
      return parseInt(
        activeStep.getAttribute('data-step'),
        10
      );
    }

    /*
     * Fallback:
     * Detect currently visible content.
     */
    const contents = document.querySelectorAll(
      '.k-popup__content[data-step]'
    );

    for (const content of contents) {
      if (isElementVisible(content)) {
        return parseInt(
          content.getAttribute('data-step'),
          10
        );
      }
    }

    return 1;
  }


  /**
   * ----------------------------------------------------------
   * STEP LOCKING
   * ----------------------------------------------------------
   */

  function updateWizardState() {
    const steps = document.querySelectorAll(
      STEP_SELECTOR
    );

    steps.forEach(function (step) {
      const stepNumber = parseInt(
        step.getAttribute('data-step'),
        10
      );

      if (stepNumber > highestReachedStep) {

        step.classList.add(LOCKED_CLASS);

        step.setAttribute(
          'aria-disabled',
          'true'
        );

        step.style.cursor = 'not-allowed';

        step.style.opacity = '0.55';

      } else {

        step.classList.remove(LOCKED_CLASS);

        step.removeAttribute(
          'aria-disabled'
        );

        step.style.cursor = 'pointer';

        step.style.opacity = '';
      }
    });
  }


  /**
   * Unlock a step after Kaktus legitimately navigates there.
   */
  function registerCurrentStep() {
    const currentStep = getCurrentStep();

    if (
      currentStep &&
      currentStep > highestReachedStep
    ) {
      highestReachedStep = currentStep;

      updateWizardState();
    }
  }


  /**
   * ----------------------------------------------------------
   * BLOCK DIRECT CLICKING OF FUTURE STEPS
   * ----------------------------------------------------------
   */

  document.addEventListener(
    'click',
    function (event) {

      const step = event.target.closest(
        STEP_SELECTOR
      );

      if (!step) {
        return;
      }

      const requestedStep = parseInt(
        step.getAttribute('data-step'),
        10
      );

      /*
       * Customer is trying to jump ahead.
       */
      if (
        requestedStep > highestReachedStep
      ) {

        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();

        return false;
      }

      /*
       * Previously reached steps remain clickable.
       *
       * Example:
       *
       * Customer reached Step 3:
       *
       * Step 1 -> allowed
       * Step 2 -> allowed
       * Step 3 -> allowed
       */
    },
    true
  );


  /**
   * ----------------------------------------------------------
   * NEXT STEP BUTTON VALIDATION
   * ----------------------------------------------------------
   */

  function isNextStepButton(element) {
    if (!element) {
      return false;
    }

    const button = element.closest(
      'button, input[type="button"], input[type="submit"]'
    );

    if (!button) {
      return false;
    }

    const text = (
      button.innerText ||
      button.textContent ||
      button.value ||
      ''
    )
      .trim()
      .toLowerCase();

    return text === 'next step';
  }


  document.addEventListener(
    'click',
    function (event) {

      if (!isNextStepButton(event.target)) {
        return;
      }

      const currentStep =
        getCurrentStep();


      /*
       * STEP 1
       *
       * We deliberately let Kaktus/browser perform
       * its existing validation here.
       *
       * Your form already correctly validates:
       *
       * Products
       * Homologation
       * etc.
       *
       * If validation fails, Kaktus will not navigate.
       * Because Step 2 isn't reached, it remains locked.
       */
      if (currentStep === 1) {
        return;
      }


      /*
       * STEP 2
       *
       * Kaktus correctly validates the textarea,
       * but its readonly Flatpickr date field does
       * not participate in native required validation.
       *
       * We therefore validate field_5 ourselves.
       */
      if (currentStep === 2) {

        if (!validateDateField()) {

          event.preventDefault();

          event.stopPropagation();

          event.stopImmediatePropagation();

          const wrapper =
            getDateWrapper();

          const input =
            getDateField();

          if (wrapper) {

            wrapper.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }


          /*
           * Open Flatpickr so customer can
           * immediately select a date.
           */
          if (input) {

            window.setTimeout(
              function () {
                input.click();
              },
              100
            );
          }

          return false;
        }
      }

    },
    true
  );


  /**
   * ----------------------------------------------------------
   * REMOVE DATE ERROR AFTER DATE SELECTION
   * ----------------------------------------------------------
   */

  document.addEventListener(
    'change',
    function (event) {

      if (
        event.target.matches(
          DATE_FIELD_SELECTOR
        )
      ) {

        if (
          (event.target.value || '')
            .trim()
        ) {

          removeDateError();
        }
      }

    },
    true
  );


  document.addEventListener(
    'input',
    function (event) {

      if (
        event.target.matches(
          DATE_FIELD_SELECTOR
        )
      ) {

        if (
          (event.target.value || '')
            .trim()
        ) {

          removeDateError();
        }
      }

    },
    true
  );


  /**
   * ----------------------------------------------------------
   * WATCH KAKTUS FOR SUCCESSFUL STEP CHANGES
   * ----------------------------------------------------------
   *
   * This is the important part.
   *
   * We DO NOT assume clicking Next means the step
   * was successfully completed.
   *
   * Instead, we wait until Kaktus itself changes
   * the active step.
   *
   * Example:
   *
   * Step 1 invalid
   * -> Kaktus stays Step 1
   * -> Step 2 remains locked
   *
   * Step 1 valid
   * -> Kaktus activates Step 2
   * -> observer detects Step 2
   * -> Step 2 becomes unlocked
   *
   * Step 2 valid
   * -> Kaktus activates Step 3
   * -> observer detects Step 3
   * -> Step 3 becomes unlocked
   *
   * If Kaktus conditional logic legitimately skips
   * Step 2 and opens Step 3, Step 3 is automatically
   * considered reached.
   */

  const observer =
    new MutationObserver(function () {

      registerCurrentStep();

    });


  function startObserver() {

    const wizard =
      document.querySelector(
        WIZARD_SELECTOR
      );

    if (!wizard) {
      return false;
    }

    observer.observe(
      wizard,
      {
        attributes: true,
        subtree: true,
        attributeFilter: [
          'class',
          'style'
        ]
      }
    );

    return true;
  }


  /**
   * ----------------------------------------------------------
   * INITIALIZATION
   * ----------------------------------------------------------
   */

  function initialize() {

    const wizard =
      document.querySelector(
        WIZARD_SELECTOR
      );

    if (!wizard) {
      return false;
    }


    /*
     * IMPORTANT:
     *
     * Start from Step 1 permissions regardless
     * of Kaktus making all wizard headings clickable.
     */
    highestReachedStep = 1;

    updateWizardState();

    startObserver();

    return true;
  }


  /**
   * Kaktus may load asynchronously.
   *
   * Try immediately first.
   */
  if (!initialize()) {

    /*
     * If Kaktus isn't loaded yet,
     * wait for it.
     */
    const pageObserver =
      new MutationObserver(
        function () {

          if (initialize()) {

            pageObserver.disconnect();
          }

        }
      );

    pageObserver.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );
  }

})();


/**
 * FYSHE - Other Product Field
 * Hide/show with CSS !important
 */

(function () {
  'use strict';

  // Add CSS rule once
  if (!document.getElementById('fyshe-other-product-css')) {

    const style = document.createElement('style');

    style.id = 'fyshe-other-product-css';

    style.textContent = `
      label[data-field-id="hhJHy"].fyshe-other-product-hidden {
        display: none !important;
      }
    `;

    document.head.appendChild(style);
  }

  function setupOtherProductField() {

    const checkbox = document.querySelector(
      'input[type="checkbox"][name="field_1"][value="other"]'
    );

    const field = document.querySelector(
      'input[name="field_12"]'
    );

    const wrapper = field
      ? field.closest('label[data-field-id="hhJHy"]')
      : null;

    if (!checkbox || !field || !wrapper) {
      return;
    }

    function updateField() {

      if (checkbox.checked) {

        wrapper.classList.remove(
          'fyshe-other-product-hidden'
        );

        field.setAttribute('required', '');

      } else {
        wrapper.classList.add(
          'fyshe-other-product-hidden'
        );

        field.removeAttribute('required');
        field.value = '';
      }
    }

    updateField();

    if (checkbox.dataset.fysheOtherSetup !== 'true') {

      checkbox.addEventListener('change', function () {

        updateField();

      });

      checkbox.dataset.fysheOtherSetup = 'true';
    }
  }

  setupOtherProductField();

  // Kaktus can rebuild the form, so keep checking
  const observer = new MutationObserver(function () {
    setupOtherProductField();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();